import { useMemo, useState } from 'react'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import {
  CalendarDays,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Info,
  Lock,
} from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useCurrency } from '@/contexts/CurrencyContext'
import apiClient from '@/api/client'
import type { BusinessDay, CashDrawerMovement } from '@/types'
import { cn, formatDateHuman, formatDateTimeHuman } from '@/lib/utils'
import { getStoredUserRole } from '@/lib/staff-roles'
import { ClosingHealthMetrics } from './ClosingHealthMetrics'
import { ZReportButton } from './ZReportButton'
import { ForceCloseDayDialog } from './ForceCloseDayDialog'
import { buildMonthDisplayRows } from './dayCloseHelpers'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import type { ReportsSearch, ReportTab } from '@/components/admin/reports/reportsRouteSearch'

// Local-TZ "YYYY-MM" for today. Used as the default month and as the
// upper bound for the next-chevron disable check.
function currentMonthStr(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

// Inclusive [first, last] ISO dates for a YYYY-MM month, e.g.
// "2026-05" → ["2026-05-01", "2026-05-31"]. Last day computed via the
// "day 0 of next month" trick so February and 30-day months both work.
function monthRangeISO(monthStr: string): [string, string] {
  const [y, m] = monthStr.split('-').map(Number)
  const lastDay = new Date(y, m, 0).getDate()
  const mm = String(m).padStart(2, '0')
  return [`${y}-${mm}-01`, `${y}-${mm}-${String(lastDay).padStart(2, '0')}`]
}

function addMonths(monthStr: string, offset: number): string {
  const [y, m] = monthStr.split('-').map(Number)
  const total = y * 12 + (m - 1) + offset
  const newY = Math.floor(total / 12)
  const newM = (total % 12) + 1
  return `${newY}-${String(newM).padStart(2, '0')}`
}

// "2026-05" → "May 2026". Uses UTC noon to dodge TZ rollover for the
// label-only formatter (we still treat the month itself as local).
function formatMonthLabel(monthStr: string): string {
  const [y, m] = monthStr.split('-').map(Number)
  const d = new Date(Date.UTC(y, m - 1, 15, 12))
  return d.toLocaleString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' })
}

/**
 * Day History page — shows the historical business days table for a
 * selected month and lets admins drill into a specific day to view its
 * full financial snapshot in a right-side drawer. Month is URL-driven
 * via `?month=YYYY-MM`; chevrons step ±1 month, disabled at today's
 * month (forward) and at the earliest business_day on record (back).
 *
 * The 4 KPI tiles above the table (ClosingHealthMetrics) deliberately
 * stay on a rolling 90-day window — they describe "how reliable is
 * closing right now" and shouldn't shift when the user pages backwards.
 */
export function DayHistoryPage() {
  const { formatCurrency } = useCurrency()
  const navigate = useNavigate()
  const search = useSearch({ from: '/admin/reports' }) as ReportsSearch

  /**
   * Force-close dialog state. Opened from the row drill-in drawer when the
   * selected day is from a prior date and still in open/staged/reopened.
   * The dialog handles its own PIN+reason mutation and invalidates day-ops
   * queries on success, which closes the drawer naturally (selectedDay's
   * status flips to 'closed' and the user can pick another).
   */
  const [forceCloseOpen, setForceCloseOpen] = useState(false)

  // Read-only roles (accountant) view day-close history but cannot mutate it.
  // The backend never grants them /day-ops/days/:id/force-close, so this just
  // hides the dead control rather than enforcing access.
  const isReadOnly = getStoredUserRole() === 'accountant'

  // YYYY-MM-DD in local time, for the prior-date guard. The backend's
  // business_date < today check is the real safety net; this is just the
  // UI not bothering to expose the button when it'd 409.
  const todayISO = useMemo(() => {
    const d = new Date()
    const y = d.getFullYear()
    const m = String(d.getMonth() + 1).padStart(2, '0')
    const dd = String(d.getDate()).padStart(2, '0')
    return `${y}-${m}-${dd}`
  }, [])

  const currentMonth = useMemo(() => currentMonthStr(), [])
  const selectedMonth =
    search.month && /^\d{4}-\d{2}$/.test(search.month) ? search.month : currentMonth
  const [fromISO, toISO] = useMemo(() => monthRangeISO(selectedMonth), [selectedMonth])
  const monthLabel = useMemo(() => formatMonthLabel(selectedMonth), [selectedMonth])

  // Rolling 90-day list — powers ClosingHealthMetrics. Kept independent
  // of the month-paginated table so the streak/missed counters stay
  // honest as the user navigates backwards through history.
  const metricsQuery = useQuery({
    queryKey: ['day-ops', 'history', { limit: 90 }],
    queryFn: () => apiClient.listDayOpsHistory({ limit: 90 }),
  })
  const metricsHistory = (metricsQuery.data?.data ?? []) as BusinessDay[]

  // Month-scoped list — powers the Business Days table and drawer
  // prev/next navigation. Refetched whenever the user pages months.
  const monthHistoryQuery = useQuery({
    queryKey: ['day-ops', 'history', 'month', selectedMonth],
    queryFn: () => apiClient.listDayOpsHistory({ from: fromISO, to: toISO }),
  })
  const monthHistory = (monthHistoryQuery.data?.data ?? []) as BusinessDay[]

  // Data boundary — the oldest business_date that exists. Used to
  // disable the back-chevron when there's no older data to page to.
  const boundsQuery = useQuery({
    queryKey: ['day-ops', 'history-bounds'],
    queryFn: () => apiClient.getDayOpsHistoryBounds(),
    staleTime: 60_000,
  })
  const earliestDate = boundsQuery.data?.data?.earliest ?? null
  const earliestMonth = useMemo(
    () => (earliestDate ? earliestDate.slice(0, 7) : null),
    [earliestDate],
  )

  // Real days merged with synthetic 'missed' rows for every calendar
  // date the month is missing, so admins can scan an unbroken month.
  // monthHistory itself stays real-only — the drawer prev/next nav
  // below depends on it skipping over missed days for free.
  const monthDisplayRows = useMemo(
    () =>
      buildMonthDisplayRows({
        month: selectedMonth,
        history: monthHistory,
        earliestDate,
        today: new Date(),
      }),
    [selectedMonth, monthHistory, earliestDate],
  )

  const monthPrevDisabled = !!earliestMonth && selectedMonth <= earliestMonth
  const monthNextDisabled = selectedMonth >= currentMonth

  const selectedDayId = search.closeId ?? null
  const selectedIndex = selectedDayId
    ? monthHistory.findIndex((d) => d.id === selectedDayId)
    : -1
  // Fallback: a closeId from a different month (deep-link, stale URL).
  // Fetch the row directly so the drawer can still render — prev/next
  // will be disabled since the day isn't in monthHistory.
  const directDayQuery = useQuery({
    queryKey: ['day-ops', 'day', selectedDayId],
    queryFn: () =>
      selectedDayId ? apiClient.getBusinessDay(selectedDayId) : Promise.resolve(null),
    enabled: !!selectedDayId && selectedIndex < 0,
  })
  const selectedDay =
    selectedIndex >= 0
      ? monthHistory[selectedIndex]
      : (directDayQuery.data?.data ?? null)

  const movementsQuery = useQuery({
    queryKey: ['day-ops', 'movements', selectedDayId],
    queryFn: () =>
      selectedDayId
        ? apiClient.listDayCashMovements(selectedDayId)
        : Promise.resolve(null),
    enabled: !!selectedDayId,
  })
  const movements = (movementsQuery.data?.data ?? []) as CashDrawerMovement[]

  const setMonth = (m: string) => {
    navigate({
      to: '/admin/reports',
      search: (prev: ReportsSearch): ReportsSearch => {
        // Drop closeId on month change — the previously-selected day
        // likely isn't in the new month's table, and we don't want a
        // dangling drawer on what looks like a fresh page.
        const { closeId: _omit, ...rest } = prev
        return { ...rest, tab: 'day-close' as ReportTab, month: m }
      },
      replace: true,
    })
  }
  const shiftMonth = (offset: number) => setMonth(addMonths(selectedMonth, offset))

  const openDrill = (id: string) => {
    navigate({
      to: '/admin/reports',
      search: (prev: ReportsSearch): ReportsSearch => ({
        ...prev,
        tab: 'day-close' as ReportTab,
        closeId: id,
      }),
      replace: true,
    })
  }

  const closeDrill = () => {
    navigate({
      to: '/admin/reports',
      search: (prev: ReportsSearch): ReportsSearch => {
        const { closeId: _omit, ...rest } = prev
        return rest
      },
      replace: true,
    })
  }

  // monthHistory is newest-first, so +1 steps to an older day in the
  // same month and −1 steps to a newer one. Arrows disable at the
  // month's edges; to cross months the user closes the drawer first.
  const shiftDrillIndex = (offset: number) => {
    if (selectedIndex < 0) return
    const next = selectedIndex + offset
    if (next < 0 || next >= monthHistory.length) return
    openDrill(monthHistory[next].id)
  }
  const prevDisabled = selectedIndex < 0 || selectedIndex >= monthHistory.length - 1
  const nextDisabled = selectedIndex < 0 || selectedIndex <= 0

  return (
    <div className="space-y-4">
      <ClosingHealthMetrics history={metricsHistory} />

      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0">
          <CardTitle className="text-base flex items-center gap-2">
            <CalendarDays className="h-4 w-4 text-muted-foreground" />
            Business Days
          </CardTitle>
          <div className="flex items-center gap-1">
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => shiftMonth(-1)}
              disabled={monthPrevDisabled}
              aria-label="Previous month"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="px-2 text-sm font-medium tabular-nums min-w-[110px] text-center">
              {monthLabel}
            </span>
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => shiftMonth(1)}
              disabled={monthNextDisabled}
              aria-label="Next month"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {monthHistoryQuery.isLoading ? (
            <Skeleton className="h-48 w-full" />
          ) : monthDisplayRows.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">
              No business days in {monthLabel}.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Gross</TableHead>
                    <TableHead className="text-right">Cash</TableHead>
                    <TableHead className="text-right">Card</TableHead>
                    <TableHead className="text-right">Online</TableHead>
                    <TableHead className="text-right">Worst variance</TableHead>
                    <TableHead>Closed by</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {monthDisplayRows.map((row) => {
                    if (row.kind === 'missed') {
                      return (
                        <TableRow key={`missed-${row.date}`} className="text-muted-foreground">
                          <TableCell className="font-medium">
                            {formatDateHuman(row.date)}
                          </TableCell>
                          <TableCell>
                            <StatusBadge state="missed" />
                          </TableCell>
                          <TableCell className="text-right tabular-nums">—</TableCell>
                          <TableCell className="text-right tabular-nums">—</TableCell>
                          <TableCell className="text-right tabular-nums">—</TableCell>
                          <TableCell className="text-right tabular-nums">—</TableCell>
                          <TableCell className="text-right tabular-nums">—</TableCell>
                          <TableCell>—</TableCell>
                          <TableCell />
                        </TableRow>
                      )
                    }
                    const d = row.day
                    return (
                      <TableRow
                        key={d.id}
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => openDrill(d.id)}
                      >
                        <TableCell className="font-medium">
                          {formatDateHuman(d.business_date)}
                        </TableCell>
                        <TableCell>
                          <StatusBadge state={d.status} />
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {d.gross_sales != null ? formatCurrency(d.gross_sales) : '—'}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {d.counted_cash != null
                            ? formatCurrency(d.counted_cash)
                            : d.expected_cash != null
                              ? formatCurrency(d.expected_cash)
                              : '—'}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {d.counted_card != null ? formatCurrency(d.counted_card) : '—'}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {d.counted_online != null ? formatCurrency(d.counted_online) : '—'}
                        </TableCell>
                        {/* F-CTRL-N33: the Variance column used to report cash
                            only, so a day whose card batch was Rs 20k short read
                            as clean in the list. It now reports the largest
                            variance across all three tenders, labelled, and the
                            drill-down breaks them out. */}
                        <TableCell
                          className={cn(
                            'text-right tabular-nums',
                            varianceToneClass(worstVariance(d).value),
                          )}
                        >
                          {(() => {
                            const w = worstVariance(d)
                            if (w.value == null) return '—'
                            return `${formatCurrency(w.value)}${w.label ? ` (${w.label})` : ''}`
                          })()}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {d.closed_by_name ?? '—'}
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center justify-end gap-1">
                            {(d.status === 'staged' || d.status === 'closed') && (
                              <ZReportButton
                                dayId={d.id}
                                enabled
                                variant="ghost"
                                size="sm"
                                label="Z"
                              />
                            )}
                            <ChevronRight
                              className="h-4 w-4 text-muted-foreground"
                              aria-hidden="true"
                            />
                          </div>
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Sheet
        open={!!selectedDay}
        onOpenChange={(open) => {
          if (!open) closeDrill()
        }}
      >
        <SheetContent
          size="xl"
          aria-describedby={undefined}
          className="p-0"
        >
          {selectedDay && (
            <>
              <SheetHeader className="gap-3 pr-9">
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => shiftDrillIndex(1)}
                    disabled={prevDisabled}
                    aria-label="Previous day"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <SheetTitle className="text-base">
                    {formatDateHuman(selectedDay.business_date)}
                  </SheetTitle>
                  <StatusBadge state={selectedDay.status} />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => shiftDrillIndex(-1)}
                    disabled={nextDisabled}
                    aria-label="Next day"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                  <div className="ml-auto flex items-center gap-2">
                    {!isReadOnly &&
                      (selectedDay.status === 'open' ||
                        selectedDay.status === 'staged' ||
                        selectedDay.status === 'reopened') &&
                      selectedDay.business_date < todayISO && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="border-rose-300 text-rose-700 hover:bg-rose-50 hover:text-rose-800 dark:border-rose-900 dark:text-rose-300 dark:hover:bg-rose-950"
                          onClick={() => setForceCloseOpen(true)}
                        >
                          <Lock className="mr-1.5 h-3.5 w-3.5" />
                          Force-close
                        </Button>
                      )}
                    {(selectedDay.status === 'staged' || selectedDay.status === 'closed') && (
                      <ZReportButton dayId={selectedDay.id} enabled variant="outline" size="sm" />
                    )}
                  </div>
                </div>
                {selectedDay.closed_by_name && (
                  <p className="text-xs text-muted-foreground">
                    Closed by {selectedDay.closed_by_name}
                  </p>
                )}
              </SheetHeader>
              <SheetBody>
                <DayDetailBody
                  day={selectedDay}
                  movements={movements}
                  movementsLoading={movementsQuery.isLoading}
                  formatCurrency={formatCurrency}
                />
              </SheetBody>
            </>
          )}
        </SheetContent>
      </Sheet>

      {selectedDay && !isReadOnly && (
        <ForceCloseDayDialog
          open={forceCloseOpen}
          onOpenChange={setForceCloseOpen}
          dayId={selectedDay.id}
          businessDate={selectedDay.business_date}
        />
      )}
    </div>
  )
}

function DayDetailBody({
  day,
  movements,
  movementsLoading,
  formatCurrency,
}: {
  day: BusinessDay
  movements: CashDrawerMovement[]
  movementsLoading: boolean
  formatCurrency: (n: number) => string
}) {
  const [showAllMovements, setShowAllMovements] = useState(false)
  const paidInTotal = useMemo(
    () =>
      movements
        .filter((m) => m.movement_type === 'paid_in')
        .reduce((sum, m) => sum + m.amount, 0),
    [movements],
  )
  const paidOutTotal = useMemo(
    () =>
      movements
        .filter((m) => m.movement_type === 'paid_out')
        .reduce((sum, m) => sum + m.amount, 0),
    [movements],
  )
  const netAdjustments = paidInTotal - paidOutTotal
  const expectedCash = day.expected_cash ?? null
  const inferredCashSales =
    expectedCash != null ? expectedCash - (day.opening_cash ?? 0) - netAdjustments : null
  const visibleMovements = showAllMovements ? movements : movements.slice(0, 5)

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Day Close Snapshot</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <SnapshotItem
            label="Gross Sales"
            helpText="Total sales amount for this day before expense deductions."
            value={day.gross_sales != null ? formatCurrency(day.gross_sales) : '—'}
          />
          <SnapshotItem
            label="Opening Cash"
            helpText="Cash in drawer at day start (usually carried from previous close)."
            value={formatCurrency(day.opening_cash ?? 0)}
          />
          <SnapshotItem
            label="Expected Cash"
            helpText="Cash that should be in drawer: opening cash + cash sales + paid in - paid out."
            value={day.expected_cash != null ? formatCurrency(day.expected_cash) : '—'}
          />
          <SnapshotItem
            label="Counted Cash"
            helpText="Actual cash physically counted in the drawer during close."
            value={day.counted_cash != null ? formatCurrency(day.counted_cash) : '—'}
          />
          <SnapshotItem
            label="Expected Card"
            helpText="What the card machine batch should total: card sales for the day plus any card taken today against orders opened on earlier days."
            value={day.expected_card != null ? formatCurrency(day.expected_card) : '—'}
          />
          <SnapshotItem
            label="Counted Card"
            helpText="Card payments counted for the day from the payment terminal/system."
            value={day.counted_card != null ? formatCurrency(day.counted_card) : 'Not counted'}
            className={day.counted_card == null ? 'text-muted-foreground' : ''}
          />
          <SnapshotItem
            label="Expected Online"
            helpText="What online and wallet receipts should total: online sales for the day plus any online taken today against orders opened on earlier days."
            value={day.expected_online != null ? formatCurrency(day.expected_online) : '—'}
          />
          <SnapshotItem
            label="Counted Online"
            helpText="Online/digital payments recorded for this day."
            value={day.counted_online != null ? formatCurrency(day.counted_online) : 'Not counted'}
            className={day.counted_online == null ? 'text-muted-foreground' : ''}
          />
          <SnapshotItem
            label="Cash Variance"
            helpText="Difference between expected cash and counted cash. Zero means perfect reconciliation."
            value={day.cash_variance != null ? formatCurrency(day.cash_variance) : '—'}
            className={varianceToneClass(day.cash_variance)}
          />
          {/* F-CTRL-N33: card / online variance were persisted but rendered
              nowhere. "Not counted" (NULL) is shown as such — a force-closed
              day must never read as though it reconciled to zero. */}
          <SnapshotItem
            label="Card Variance"
            helpText="Counted card total minus expected. Blank means this tender was never counted (force-closed day)."
            value={day.card_variance != null ? formatCurrency(day.card_variance) : 'Not counted'}
            className={varianceToneClass(day.card_variance)}
          />
          <SnapshotItem
            label="Online Variance"
            helpText="Counted online total minus expected. Blank means this tender was never counted (force-closed day)."
            value={day.online_variance != null ? formatCurrency(day.online_variance) : 'Not counted'}
            className={varianceToneClass(day.online_variance)}
          />
        </div>
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-4 border-t pt-4">
          <SnapshotItem label="Opened by" value={day.opened_by_name ?? '—'} />
          <SnapshotItem label="Opened at" value={day.opened_at ? formatDateTimeHuman(day.opened_at) : '—'} />
          {day.staged_at && (
            <SnapshotItem label="Staged at" value={formatDateTimeHuman(day.staged_at)} />
          )}
          {day.closed_at && (
            <SnapshotItem label="Closed at" value={formatDateTimeHuman(day.closed_at)} />
          )}
        </div>

        <div className="mt-4 border-t pt-4 space-y-3">
          <div className="text-sm font-semibold">Why this variance?</div>
          {day.closing_notes && day.closing_notes.trim() && (
            <div className="rounded-lg border bg-muted/30 px-3 py-2.5">
              <div className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground mb-1">
                Manager note
              </div>
              <div className="text-sm whitespace-pre-wrap break-words">
                {day.closing_notes}
              </div>
            </div>
          )}
          <div className="grid gap-1 text-sm">
            <ReconciliationLine label="Opening cash" value={formatCurrency(day.opening_cash ?? 0)} />
            <ReconciliationLine
              label="+ Cash sales"
              value={inferredCashSales != null ? formatCurrency(inferredCashSales) : '—'}
            />
            <ReconciliationLine label="+ Paid in" value={formatCurrency(paidInTotal)} />
            <ReconciliationLine label="− Paid out" value={formatCurrency(paidOutTotal)} />
            <ReconciliationLine
              label="= Expected cash"
              value={expectedCash != null ? formatCurrency(expectedCash) : '—'}
              bold
            />
            <ReconciliationLine
              label="Counted cash"
              value={day.counted_cash != null ? formatCurrency(day.counted_cash) : '—'}
            />
            <ReconciliationLine
              label="Variance (counted - expected)"
              value={day.cash_variance != null ? formatCurrency(day.cash_variance) : '—'}
              bold
              className={
                day.cash_variance != null
                  ? day.cash_variance < -0.01
                    ? 'text-rose-600'
                    : day.cash_variance > 0.01
                      ? 'text-amber-600'
                      : 'text-emerald-600'
                  : undefined
              }
            />
          </div>
          {movementsLoading ? (
            <Skeleton className="h-20 w-full" />
          ) : movements.length > 0 ? (
            <div className="rounded-lg border">
              <div className="px-3 py-2 border-b text-xs uppercase tracking-wide text-muted-foreground">
                Movements used in calculation
              </div>
              <div className="divide-y">
                {visibleMovements.map((m) => (
                  <div key={m.id} className="px-3 py-2 flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2">
                      <Badge
                        variant="outline"
                        className={cn(
                          'text-[10px] capitalize',
                          m.movement_type === 'paid_in'
                            ? 'border-emerald-300 text-emerald-700'
                            : 'border-rose-300 text-rose-700',
                        )}
                      >
                        {m.movement_type.replace('_', ' ')}
                      </Badge>
                      <span>{m.reason}</span>
                    </div>
                    <span
                      className={cn(
                        'font-semibold tabular-nums',
                        m.movement_type === 'paid_in' ? 'text-emerald-700' : 'text-rose-700',
                      )}
                    >
                      {m.movement_type === 'paid_in' ? '+' : '−'}
                      {formatCurrency(m.amount)}
                    </span>
                  </div>
                ))}
              </div>
              {movements.length > 5 && (
                <div className="p-2 border-t">
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 px-2 text-xs"
                    onClick={() => setShowAllMovements((v) => !v)}
                  >
                    {showAllMovements ? (
                      <>
                        <ChevronUp className="h-3.5 w-3.5 mr-1" /> Show fewer
                      </>
                    ) : (
                      <>
                        <ChevronDown className="h-3.5 w-3.5 mr-1" /> Show all movements ({movements.length})
                      </>
                    )}
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <div className="text-xs text-muted-foreground">No paid-in / paid-out movements for this day.</div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

function ReconciliationLine({
  label,
  value,
  bold = false,
  className,
}: {
  label: string
  value: string
  bold?: boolean
  className?: string
}) {
  return (
    <div className="flex items-center justify-between">
      <span className={cn('text-muted-foreground', bold && 'font-medium text-foreground')}>{label}</span>
      <span className={cn('tabular-nums', bold ? 'font-semibold' : 'font-medium', className)}>{value}</span>
    </div>
  )
}

function StatusBadge({ state }: { state: BusinessDay['status'] | 'missed' | null }) {
  const map: Record<string, { label: string; cls: string }> = {
    open: {
      label: 'Open',
      cls: 'border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300',
    },
    staged: {
      label: 'Staged',
      cls: 'border-amber-300 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-950/60 dark:text-amber-300',
    },
    closed: {
      label: 'Closed',
      cls: 'border-muted bg-muted text-muted-foreground',
    },
    reopened: {
      label: 'Reopened',
      cls: 'border-amber-300 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-950/60 dark:text-amber-300',
    },
    missed: {
      label: 'Missed',
      cls: 'border-rose-300 bg-rose-50 text-rose-700 dark:border-rose-800 dark:bg-rose-950/60 dark:text-rose-300',
    },
  }
  const { label, cls } = map[state ?? ''] ?? {
    label: '—',
    cls: 'border-muted bg-muted text-muted-foreground',
  }
  return (
    <Badge variant="outline" className={cn('text-[10px] uppercase tracking-wide', cls)}>
      {label}
    </Badge>
  )
}

function SnapshotItem({
  label,
  helpText,
  value,
  className,
}: {
  label: string
  helpText?: string
  value: string
  className?: string
}) {
  return (
    <div className="rounded-lg border bg-muted/20 px-3 py-2">
      <div className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        <span>{label}</span>
        {helpText ? (
          <TooltipProvider delayDuration={150}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  className="inline-flex text-muted-foreground/80 hover:text-foreground"
                  aria-label={`What is ${label}?`}
                >
                  <Info className="h-3.5 w-3.5" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-[240px] text-left">
                {helpText}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : null}
      </div>
      <div className={cn('text-sm font-semibold tabular-nums', className)}>{value}</div>
    </div>
  )
}

/**
 * Largest absolute variance across cash / card / online, with the tender named
 * when it isn't cash (F-CTRL-N33).
 *
 * The history list used to print `cash_variance` alone under a "Variance"
 * heading, so a day whose card batch was thousands short read as perfectly
 * reconciled at a glance — card and online variance were persisted but shown on
 * no screen at all. NULL is skipped rather than treated as 0: a tender nobody
 * counted (force-close) must not be reported as a clean reconciliation.
 */
function worstVariance(d: BusinessDay): { value: number | null; label: string | null } {
  const candidates: Array<{ v: number | null | undefined; label: string | null }> = [
    { v: d.cash_variance, label: null },
    { v: d.card_variance, label: 'card' },
    { v: d.online_variance, label: 'online' },
  ]
  let best: { value: number | null; label: string | null } = { value: null, label: null }
  for (const c of candidates) {
    if (c.v == null) continue
    if (best.value == null || Math.abs(c.v) > Math.abs(best.value)) {
      best = { value: c.v, label: c.label }
    }
  }
  return best
}

/** Rose past the Rs 100 threshold, green when it reconciles, neutral otherwise. */
function varianceToneClass(variance: number | null | undefined): string {
  if (variance == null) return 'text-muted-foreground'
  const abs = Math.abs(variance)
  if (abs > 100) return 'text-rose-600'
  if (abs < 0.01) return 'text-emerald-600'
  return ''
}
